import { Box, Stack, Typography } from "@mui/material";
import { TreeViewBaseItem } from "@mui/x-tree-view/models";
import { RichTreeView } from "@mui/x-tree-view/RichTreeView";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { getAutoExpandedItems } from "../../../ObservationSection/components/ObservationTypeTree/utils";
import { MapEditorFeatureCreateOptionsSelect_EcologicalFunctionFragment } from "../../fragments.generated";

type EcologicalFunctionTypeTreeProps = {
    researchTypes: MapEditorFeatureCreateOptionsSelect_EcologicalFunctionFragment[];
    selectedEcologicalFunctionTypeId: string | null;
    onSelectionChange: (itemId: string | null) => void;
};

function mapToTreeNodes(
    researchTypes: MapEditorFeatureCreateOptionsSelect_EcologicalFunctionFragment[],
): TreeViewBaseItem[] {
    return researchTypes.map(researchType => ({
        id: researchType.id,
        label: researchType.name,
        children: researchType.ecologicalFunctionTypes
            .slice()
            .sort((a, b) => a.order - b.order)
            .map(ecologicalFunctionType => ({
                id: ecologicalFunctionType.id,
                label: ecologicalFunctionType.name,
            })),
    }));
}

export function EcologicalFunctionTypeTree({
    researchTypes,
    selectedEcologicalFunctionTypeId,
    onSelectionChange,
}: EcologicalFunctionTypeTreeProps) {
    const { t } = useTranslation("mapEditor");

    const [expandedItems, setExpandedItems] = useState<string[]>([]);

    const treeItems = useMemo(() => mapToTreeNodes(researchTypes), [researchTypes]);

    useEffect(() => {
        const autoExpanded = getAutoExpandedItems(treeItems);
        setExpandedItems(autoExpanded);
    }, [treeItems]);

    const handleExpandedItemsChange = (_: unknown, itemIds: string[]) => {
        if (itemIds.length > expandedItems.length) {
            const newlyExpanded = itemIds.find(id => !expandedItems.includes(id));
            setExpandedItems(newlyExpanded ? [newlyExpanded] : []);
        } else {
            setExpandedItems(itemIds);
        }
    };

    const handleSelectionChange = (_: unknown, itemIds: string | string[] | null) => {
        const selectedId = Array.isArray(itemIds) ? itemIds[0] : itemIds;
        onSelectionChange(selectedId ?? null);
    };

    return (
        <Stack rowGap={1}>
            <Typography variant="subtitle2">
                {t("mapEditorFeatureCreateDialog.ecologicalFunctionSection.labelSelectEcologicalFunctionType")}
            </Typography>
            <Box
                sx={theme => ({
                    maxHeight: 300,
                    overflowY: "auto",
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                })}
            >
                <RichTreeView
                    items={treeItems}
                    selectedItems={selectedEcologicalFunctionTypeId ?? undefined}
                    onSelectedItemsChange={handleSelectionChange}
                    expandedItems={expandedItems}
                    onExpandedItemsChange={handleExpandedItemsChange}
                />
            </Box>
        </Stack>
    );
}
